import React, { useState, useEffect } from 'react';
import CompanyProfileService from '@/services/CompanyProfileService';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { ExternalLink, LogOut, PlusCircle } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { supabase, fundingRequirements } from '@/integrations/supabase/client';
import { AssignedDocuments } from '@/components/shared/AssignedDocuments';
import { ConversationsTab } from '@/components/conversations/ConversationsTab';

interface Business {
  id: string;
  business_name: string;
  company_name: string;
  tagline: string;
  description: string;
  logo_url: string | null;
  development_stage: string;
  industry_type: string;
  created_at: string;
  updated_at: string;
  user_id: string;
}

const EntrepreneurPage = () => {
  const navigate = useNavigate();
  // Constants for form selects
  const INDUSTRY_TYPES = [
    'Technology',
    'Healthcare',
    'Finance',
    'Education',
    'Retail',
    'Manufacturing',
    'Agriculture',
    'Energy',
    'Entertainment',
    'Food & Beverage',
    'Transportation',
    'Real Estate',
    'Other'
  ];

  const DEVELOPMENT_STAGES = [
    'Idea',
    'MVP',
    'Development',
    'Early Revenue',
    'Growth',
    'Expansion',
    'Mature'
  ];

  // State hooks
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-businesses');
  const [myBusinesses, setMyBusinesses] = useState<Business[]>([]);
  const [businessesLoading, setBusinessesLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<{ full_name: string; email: string; } | null>(null);
  
  // New business state
  const [newBusiness, setNewBusiness] = useState({
    business_name: '',
    company_name: '',
    tagline: '',
    description: '',
    development_stage: 'Idea',
    industry_type: 'Technology',
    website: '',
    contact_email: '',
    entity_type: 'business',
    status: 'active'
  });
  const [isAddingBusiness, setIsAddingBusiness] = useState(false);
  
  // Funding rounds state
  const [fundingRounds, setFundingRounds] = useState<any[]>([]);
  const [fundingLoading, setFundingLoading] = useState(true);
  const [newFundingRound, setNewFundingRound] = useState({
    business_id: '',
    amount: 100000,
    next_step: ''
  });
  const [isCreatingFunding, setIsCreatingFunding] = useState(false);
  
  // Context hooks
  const { user, signOut } = useAuth();

  // Effect hooks
  useEffect(() => {
    // Simulate loading to ensure components are properly mounted
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (user) {
      fetchMyBusinesses(user.id);
      fetchUserProfile(user.id);
      fetchFundingRounds(user.id);
    }
  }, [user]);

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        return;
      }

      setUserProfile(data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };
  
  const fetchFundingRounds = async (userId: string) => {
    try {
      setFundingLoading(true);
      
      // First get all businesses associated with this user
      const { data: businesses, error: businessError } = await supabase
        .from('businesses')
        .select('id')
        .eq('user_id', userId);
        
      if (businessError) {
        console.error('Error fetching businesses for funding rounds:', businessError);
        throw businessError;
      }
      
      if (!businesses || businesses.length === 0) {
        setFundingRounds([]);
        return;
      }
      
      // Get all business IDs
      const businessIds = businesses.map(b => b.id);
      
      // Fetch funding requirements for these businesses
      const { data: rounds, error: roundsError } = await fundingRequirements()
        .select('*')
        .in('business_id', businessIds)
        .order('created_at', { ascending: false });
        
      if (roundsError) {
        console.error('Error fetching funding rounds:', roundsError);
        throw roundsError;
      }
      
      console.log('Fetched funding rounds:', rounds);
      setFundingRounds(rounds || []);
    } catch (error) {
      console.error('Error fetching funding rounds:', error);
      toast.error('Failed to load funding rounds');
      setFundingRounds([]);
    } finally {
      setFundingLoading(false);
    }
  };

  const fetchMyBusinesses = async (userId: string) => {
    try {
      setBusinessesLoading(true);
      console.log('Fetching businesses for user ID:', userId);
      
      // First try to fetch businesses directly owned by the user
      const { data: ownedBusinesses, error: ownedError } = await supabase
        .from('businesses')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')  // Only fetch active businesses
        .order('created_at', { ascending: false });

      if (ownedError) {
        console.error('Error fetching owned businesses:', ownedError);
        toast.error('Failed to load your businesses');
        return;
      }
      
      console.log('Owned businesses:', ownedBusinesses?.length || 0);
      
      // Set businesses immediately if we have them
      if (ownedBusinesses && ownedBusinesses.length > 0) {
        setMyBusinesses(ownedBusinesses);
        setBusinessesLoading(false);
        return;
      }
      
      // If no owned businesses, check for team memberships
      if (userProfile?.email) {
        const { data: teamMembers, error: teamError } = await supabase
          .from('company_team_members')
          .select('*')
          .ilike('email', userProfile.email);
          
        if (!teamError && teamMembers && teamMembers.length > 0) {
          console.log('Found team memberships:', teamMembers.length);
          
          const companyIds = teamMembers.map(member => member.company_pitch_deck_id);
          
          const { data: linkedData, error: businessesError } = await supabase
            .from('businesses')
            .select('*')
            .in('id', companyIds)
            .eq('status', 'active')
            .order('created_at', { ascending: false });
            
          if (!businessesError && linkedData) {
            console.log('Found linked businesses:', linkedData.length);
            setMyBusinesses(linkedData);
          } else if (businessesError) {
            console.error('Error fetching linked businesses:', businessesError);
            toast.error('Failed to load team businesses');
          }
        }
      }
      
      // If we get here and still have no businesses, show empty state
      if (myBusinesses.length === 0) {
        console.log('No businesses found for user');
        toast.info('No businesses found. Create one to get started!');
      }
    } catch (error) {
      console.error('Error in fetchMyBusinesses:', error);
      toast.error('Failed to load businesses');
    } finally {
      setBusinessesLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 space-y-8 bg-white">
        <Skeleton className="h-10 w-64 mb-8" />
        <Skeleton className="h-8 w-full max-w-md mb-6" />
        <div className="space-y-6">
          <Skeleton className="h-[400px] w-full rounded-lg" />
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    await signOut();
  };
  
  const handleAddBusiness = async () => {
    try {
      // Validate required fields
      if (!newBusiness.business_name) {
        toast.error('Business name is required');
        return;
      }
      if (!newBusiness.description) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      setIsAddingBusiness(true);
      
      console.log('New Business Data:', newBusiness);
      
      // Prepare the business data
      const businessData = {
        ...newBusiness,
        // If company_name is empty, use business_name
        company_name: newBusiness.company_name || newBusiness.business_name,
        // Set the user_id to the current user
        user_id: user?.id
      };
      
      console.log('Creating new business:', businessData);
      
      // Create the business using CompanyProfileService to ensure proper status handling
      try {
        await CompanyProfileService.createCompanyProfile({
          company_name: newBusiness.business_name, // Use business_name as company_name
          tagline: newBusiness.tagline || null,
          description: newBusiness.description || null,
          development_stage: 'development',
          industry_type: newBusiness.industry_type || null,
          website: newBusiness.website || null,
          contact_email: newBusiness.contact_email || null,
          entity_type: 'business',
          status: 'draft',
          // Initialize other required fields as null
          logo_url: null,
          pitch_deck_url: null,
          problem_statement: null,
          solution_description: null,
          market_size: null,
          funding_goal: null,
          investment_highlights: null,
          competitive_edge: null,
          target_segment: null,
          growth_drivers: null,
          team_members: null,
          business_model: null,
          traction: null,
          product_offerings: null,
          financial_projections: null
        }, user?.id || '');
        
        console.log('Business created successfully');
        // Refresh the businesses list
        if (user?.id) {
          await fetchMyBusinesses(user.id);
        }
      } catch (error) {
        console.error('Error creating business:', error);
        toast.error('Failed to create business');
        return;
      }
      
      console.log('Business created successfully');
      toast.success('Business created successfully');
      
      // Reset the form
      setNewBusiness({
        business_name: '',
        company_name: '',
        tagline: '',
        description: '',
        development_stage: 'Idea',
        industry_type: 'Technology',
        website: '',
        contact_email: '',
        entity_type: 'business',
        status: 'active'
      });
      
      // Refresh the businesses list
      fetchMyBusinesses(user?.id || '');
      
      // Close the dialog (this will happen automatically when the component re-renders)
    } catch (error) {
      console.error('Error creating business:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsAddingBusiness(false);
    }
  };
  
  const handleStartFundingRound = (businessId: string) => {
    // Set the business ID in the new funding round form
    setNewFundingRound({
      ...newFundingRound,
      business_id: businessId
    });
    
    // Switch to the funding tab
    setActiveTab('funding');
    
    // Find the tab element and click it
    const fundingTab = document.querySelector('[value="funding"]') as HTMLElement;
    if (fundingTab) {
      fundingTab.click();
    }
  };

  const handleEditBusiness = (businessId: string) => {
    // Navigate to edit page
    navigate(`/company/${businessId}/edit`);
  };

  const handleViewDetails = (businessId: string) => {
    // Navigate to details page
    navigate(`/company/${businessId}`);
  };
  
  const handleCreateFundingRound = async () => {
    try {
      // Validate required fields
      if (!newFundingRound.business_id || !newFundingRound.next_step || !newFundingRound.amount) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      setIsCreatingFunding(true);
      
      console.log('Creating new funding round:', newFundingRound);
      
      // Insert the new funding requirement
      const { data, error } = await fundingRequirements()
        .insert({
          business_id: newFundingRound.business_id,
          amount: newFundingRound.amount,
          next_step: newFundingRound.next_step,
          fund_allocation_image: null
        })
        .select()
        .single();
        
      if (error) {
        console.error('Error creating funding round:', error);
        toast.error('Failed to create funding round');
        return;
      }
      
      console.log('Funding round created successfully:', data);
      toast.success('Funding round created successfully');
      
      // Reset the form
      setNewFundingRound({
        business_id: '',
        amount: 100000,
        next_step: ''
      });
      
      // Refresh the funding rounds list
      fetchFundingRounds(user?.id || '');
      
      // Close the dialog (this will happen automatically when the component re-renders)
    } catch (error) {
      console.error('Error creating funding round:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsCreatingFunding(false);
    }
  };

  return (
    <div className="container mx-auto pt-12 pb-8 px-8 space-y-8 bg-background">
      <div className="flex flex-col space-y-6 mb-8">
        <div className="flex items-center gap-4 mb-2">
          <Logo className="h-12 w-auto" />
          <h1 className="text-3xl font-bold">Entrepreneur Dashboard</h1>
        </div>
        
        <div className="text-xl font-medium">
          Hey, {userProfile?.full_name || user?.email?.split('@')[0] || 'Entrepreneur'}! Welcome back.
        </div>
        
        <div className="flex justify-between items-center">
          <p className="text-muted-foreground">Manage your businesses and funding opportunities</p>
          <div className="flex space-x-4">
            <Button variant="outline" asChild>
              <Link to="/" className="flex items-center gap-2">
                <ExternalLink size={18} />
                View Website
              </Link>
            </Button>
            <Button variant="destructive" onClick={handleLogout} className="flex items-center gap-2">
              <LogOut size={18} />
              Logout
            </Button>
          </div>
        </div>
      </div>
      
      {/* Dashboard Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="bg-card border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              My Businesses
            </CardTitle>
            <CardDescription>Total businesses you manage</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{myBusinesses.length}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {myBusinesses.length === 1 ? '1 business' : `${myBusinesses.length} businesses`} in your portfolio
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Funding Status
            </CardTitle>
            <CardDescription>Current funding progress</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Not Started</div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Investor Interest
            </CardTitle>
            <CardDescription>Investors viewing your businesses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">0</div>
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="my-businesses" className="space-y-4">
        <TabsList>
          <TabsTrigger value="my-businesses">My Businesses</TabsTrigger>
          <TabsTrigger value="funding">Funding Opportunities</TabsTrigger>
          <TabsTrigger value="documents">My Documents</TabsTrigger>
          <TabsTrigger value="conversations">Conversations</TabsTrigger>
        </TabsList>

        <TabsContent value="my-businesses" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">My Businesses</h2>
            <Button 
              className="flex items-center gap-2"
              onClick={() => toast.info("This feature is coming soon! Stay tuned for updates.")}
            >
              <PlusCircle className="h-4 w-4" />
              Add Business
            </Button>
          </div>
          
          {businessesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2].map((i) => (
                <Card key={i} className="bg-card border border-border">
                  <CardHeader>
                    <div className="w-full h-4 bg-secondary/40 rounded animate-pulse mb-2"></div>
                    <div className="w-2/3 h-3 bg-secondary/40 rounded animate-pulse"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="w-full h-20 bg-secondary/30 rounded animate-pulse"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : myBusinesses.length === 0 ? (
            <Card className="bg-card border border-border">
              <CardContent className="pt-6 text-center">
                <p className="text-muted-foreground">You haven't added any businesses yet.</p>
                <p className="text-muted-foreground">Click the "Add Business" button to get started.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {myBusinesses.map((business) => (
                <Card key={business.id} className="bg-card border border-border">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>{business.business_name}</CardTitle>
                      {business.user_id !== user?.id && (
                        <div className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          Linked Business
                        </div>
                      )}
                    </div>
                    <CardDescription>{business.tagline}</CardDescription>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <div className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                        {business.industry_type}
                      </div>
                      <div className="px-2 py-1 bg-secondary/20 text-secondary-foreground text-xs rounded-full">
                        {business.development_stage}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-card-foreground line-clamp-3 mb-4">
                      {business.description}
                    </p>
                    <div className="flex justify-end space-x-2 mt-4">
                      <Button variant="outline" size="sm" onClick={() => handleEditBusiness(business.id)}>Edit</Button>
                      <Button variant="outline" size="sm" onClick={() => handleStartFundingRound(business.id)}>Start Funding</Button>
                      <Button size="sm" onClick={() => handleViewDetails(business.id)}>View Details</Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="funding" className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Funding Opportunities</h2>
            <Dialog>
              <DialogTrigger asChild>
                <Button className="flex items-center gap-2">
                  <PlusCircle className="h-4 w-4" />
                  Start Funding Round
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Start Funding Round</DialogTitle>
                  <DialogDescription>
                    Create a new funding round to attract investors to your business.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-1 gap-2">
                    <Label htmlFor="business_select">Select Business</Label>
                    <Select
                      value={newFundingRound.business_id}
                      onValueChange={(value) => setNewFundingRound({...newFundingRound, business_id: value})}
                    >
                      <SelectTrigger id="business_select">
                        <SelectValue placeholder="Select a business" />
                      </SelectTrigger>
                      <SelectContent>
                        {myBusinesses.map((business) => (
                          <SelectItem key={business.id} value={business.id}>{business.business_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <Label htmlFor="funding_amount">Funding Amount Required ($)</Label>
                    <Input
                      id="funding_amount"
                      type="number"
                      placeholder="100000"
                      value={newFundingRound.amount}
                      onChange={(e) => setNewFundingRound({...newFundingRound, amount: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <Label htmlFor="next_step">Next Steps</Label>
                    <Textarea
                      id="next_step"
                      placeholder="Describe how you plan to use the funds"
                      value={newFundingRound.next_step}
                      onChange={(e) => setNewFundingRound({...newFundingRound, next_step: e.target.value})}
                      rows={4}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" onClick={handleCreateFundingRound} disabled={isCreatingFunding}>
                    {isCreatingFunding ? "Creating..." : "Start Funding Round"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          
          {fundingLoading ? (
            <div className="space-y-4">
              <Card className="bg-card border border-border">
                <CardHeader className="pb-2">
                  <div className="w-full h-4 bg-secondary/40 rounded animate-pulse mb-2"></div>
                  <div className="w-2/3 h-3 bg-secondary/40 rounded animate-pulse"></div>
                </CardHeader>
                <CardContent>
                  <div className="w-full h-20 bg-secondary/30 rounded animate-pulse"></div>
                </CardContent>
              </Card>
            </div>
          ) : fundingRounds.length === 0 ? (
            <Card className="bg-card border border-border">
              <CardContent className="pt-6">
                <div className="text-center p-8">
                  <h3 className="text-xl font-semibold mb-2">No Active Funding Rounds</h3>
                  <p className="text-muted-foreground mb-6">
                    You don't have any active funding rounds at the moment. 
                    Start a funding round to attract investors to your business.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {fundingRounds.map((round) => {
                const business = myBusinesses.find(b => b.id === round.business_id);
                return (
                  <Card key={round.id} className="bg-card border border-border">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>{business?.business_name || 'Business'} Funding Round</CardTitle>
                        <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                          Active
                        </Badge>
                      </div>
                      <CardDescription>Created on {new Date(round.created_at).toLocaleDateString()}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-medium mb-1">Funding Amount</h4>
                          <p className="text-xl font-bold">${round.amount.toLocaleString()}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium mb-1">Next Steps</h4>
                          <p className="text-sm">{round.next_step}</p>
                        </div>
                        <div className="flex justify-end space-x-2 mt-4">
                          <Button variant="outline" size="sm">Edit</Button>
                          <Button size="sm">View Details</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <h2 className="text-2xl font-bold">My Documents</h2>
          <AssignedDocuments />
        </TabsContent>

        <TabsContent value="conversations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Conversations</CardTitle>
              <CardDescription>Communicate with investors and administrators</CardDescription>
            </CardHeader>
            <CardContent>
              {user && <ConversationsTab userId={user.id} userRole="entrepreneur" />}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EntrepreneurPage;
